import type { ConsentManagerConfig } from '@/core/types';

export class ModalStyles {
  public static getStyles(config: ConsentManagerConfig): string {
    const theme = config.theme || 'light';

    return `
      :host {
        all: initial;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        color: ${this.getColor(theme, 'text')};
        z-index: 2147483647;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
        box-sizing: border-box;
      }

      :host(.cmp-modal--visible) {
        opacity: 1;
        visibility: visible;
      }

      .cmp-modal__backdrop {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        overflow-y: auto;
      }

      .cmp-modal__container {
        background: ${this.getColor(theme, 'background')};
        border-radius: 12px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        max-width: 600px;
        width: 100%;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
        transform: scale(0.9);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }

      :host(.cmp-modal--visible) .cmp-modal__container {
        transform: scale(1);
      }

      .cmp-modal__header {
        padding: 24px 24px 16px 24px;
        border-bottom: 1px solid ${this.getColor(theme, 'border')};
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
      }

      .cmp-modal__title {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: ${this.getColor(theme, 'text')};
      }

      .cmp-modal__close {
        background: transparent;
        border: none;
        color: ${this.getColor(theme, 'textSecondary')};
        cursor: pointer;
        padding: 8px;
        border-radius: 6px;
        transition: all 0.2s ease;
        font-family: inherit;
      }

      .cmp-modal__close:hover {
        color: ${this.getColor(theme, 'text')};
        background: ${this.getColor(theme, 'buttonSecondaryHover')};
      }

      .cmp-modal__close:focus {
        outline: 2px solid ${this.getColor(theme, 'primary')};
        outline-offset: 2px;
      }

      .cmp-modal__body {
        padding: 24px;
        overflow-y: auto;
        flex: 1;
      }

      .cmp-modal__description {
        margin: 0 0 24px 0;
        color: ${this.getColor(theme, 'textSecondary')};
        line-height: 1.6;
      }

      .cmp-modal__categories {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .cmp-modal__category {
        border: 1px solid ${this.getColor(theme, 'border')};
        border-radius: 8px;
        padding: 20px;
        background: ${this.getColor(theme, 'cardBackground')};
      }

      .cmp-modal__category-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
      }

      .cmp-modal__category-info {
        flex: 1;
        min-width: 0;
      }

      .cmp-modal__category-name {
        margin: 0 0 6px 0;
        font-size: 16px;
        font-weight: 600;
        color: ${this.getColor(theme, 'text')};
      }

      .cmp-modal__category-description {
        margin: 0;
        color: ${this.getColor(theme, 'textSecondary')};
        line-height: 1.4;
      }

      .cmp-modal__toggle-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }

      .cmp-modal__toggle {
        position: relative;
        display: inline-block;
        width: 48px;
        height: 24px;
        cursor: pointer;
      }

      .cmp-modal__toggle-input {
        opacity: 0;
        width: 0;
        height: 0;
        position: absolute;
      }

      .cmp-modal__toggle-input:disabled + .cmp-modal__toggle-slider {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .cmp-modal__toggle-slider {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${this.getColor(theme, 'toggleOff')};
        transition: 0.3s;
        border-radius: 24px;
      }

      .cmp-modal__toggle-slider:before {
        position: absolute;
        content: "";
        height: 20px;
        width: 20px;
        left: 2px;
        bottom: 2px;
        background-color: white;
        transition: 0.3s;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }

      .cmp-modal__toggle-input:checked + .cmp-modal__toggle-slider {
        background-color: ${this.getColor(theme, 'primary')};
      }

      .cmp-modal__toggle-input:focus + .cmp-modal__toggle-slider {
        box-shadow: 0 0 0 2px ${this.getColor(theme, 'primary')}40;
      }

      .cmp-modal__toggle-input:checked + .cmp-modal__toggle-slider:before {
        transform: translateX(24px);
      }

      .cmp-modal__required-badge {
        font-size: 11px;
        font-weight: 600;
        color: ${this.getColor(theme, 'primary')};
        background: ${this.getColor(theme, 'primaryLight')};
        padding: 2px 6px;
        border-radius: 4px;
        text-transform: uppercase;
        letter-spacing: 0.025em;
      }

      .cmp-modal__category-details {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid ${this.getColor(theme, 'border')};
      }

      .cmp-modal__purposes-title {
        margin: 0 0 8px 0;
        font-size: 14px;
        font-weight: 600;
        color: ${this.getColor(theme, 'text')};
      }

      .cmp-modal__purposes-list {
        margin: 0;
        padding: 0;
        list-style: none;
      }

      .cmp-modal__purpose-item {
        padding: 4px 0;
        color: ${this.getColor(theme, 'textSecondary')};
        font-size: 13px;
        position: relative;
        padding-left: 16px;
      }

      .cmp-modal__purpose-item:before {
        content: "•";
        position: absolute;
        left: 0;
        color: ${this.getColor(theme, 'primary')};
      }

      .cmp-modal__cookie-details {
        margin-top: 12px;
      }

      .cmp-modal__cookie-summary {
        font-size: 13px;
        color: ${this.getColor(theme, 'primary')};
        cursor: pointer;
        padding: 4px 0;
        font-weight: 500;
      }

      .cmp-modal__cookie-summary:hover {
        text-decoration: underline;
      }

      .cmp-modal__cookie-list {
        margin-top: 8px;
        padding: 12px;
        background: ${this.getColor(theme, 'codeBackground')};
        border-radius: 4px;
        border: 1px solid ${this.getColor(theme, 'border')};
      }

      .cmp-modal__cookie-item {
        font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
        font-size: 12px;
        color: ${this.getColor(theme, 'textSecondary')};
        padding: 2px 0;
        word-break: break-all;
      }

      .cmp-modal__footer {
        padding: 16px 24px 24px 24px;
        border-top: 1px solid ${this.getColor(theme, 'border')};
        flex-shrink: 0;
      }

      .cmp-modal__actions {
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        flex-wrap: wrap;
        margin-bottom: 16px;
      }

      .cmp-modal__button {
        border: none;
        border-radius: 6px;
        padding: 10px 20px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
        font-family: inherit;
      }

      .cmp-modal__button:focus {
        outline: 2px solid ${this.getColor(theme, 'primary')};
        outline-offset: 2px;
      }

      .cmp-modal__button--primary {
        background: ${this.getColor(theme, 'primary')};
        color: white;
      }

      .cmp-modal__button--primary:hover {
        background: ${this.getColor(theme, 'primaryHover')};
        transform: translateY(-1px);
      }

      .cmp-modal__button--secondary {
        background: ${this.getColor(theme, 'buttonSecondary')};
        color: ${this.getColor(theme, 'text')};
        border: 1px solid ${this.getColor(theme, 'border')};
      }

      .cmp-modal__button--secondary:hover {
        background: ${this.getColor(theme, 'buttonSecondaryHover')};
        transform: translateY(-1px);
      }

      .cmp-modal__branding {
        text-align: center;
        font-size: 12px;
        color: ${this.getColor(theme, 'textSecondary')};
        margin-top: 8px;
        padding-top: 12px;
        border-top: 1px solid ${this.getColor(theme, 'border')};
      }

      .cmp-modal__branding a {
        color: ${this.getColor(theme, 'primary')};
        text-decoration: none;
        font-weight: 500;
      }

      .cmp-modal__branding a:hover {
        text-decoration: underline;
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      @media (max-width: 768px) {
        .cmp-modal__backdrop {
          padding: 10px;
        }

        .cmp-modal__container {
          max-height: 95vh;
        }

        .cmp-modal__header {
          padding: 16px;
        }

        .cmp-modal__title {
          font-size: 18px;
        }

        .cmp-modal__body {
          padding: 16px;
        }

        .cmp-modal__category-header {
          flex-direction: column;
          align-items: flex-start;
          gap: 12px;
        }

        .cmp-modal__toggle-wrapper {
          align-self: flex-end;
        }

        .cmp-modal__footer {
          padding: 16px;
        }

        .cmp-modal__actions {
          flex-direction: column;
        }

        .cmp-modal__button {
          width: 100%;
          justify-content: center;
        }
      }

      /* Accessibility improvements */
      @media (prefers-reduced-motion: reduce) {
        :host,
        .cmp-modal__container,
        .cmp-modal__button,
        .cmp-modal__close,
        .cmp-modal__toggle-slider,
        .cmp-modal__toggle-slider:before {
          transition: none;
        }
      }

      @media (prefers-contrast: high) {
        .cmp-modal__container {
          border: 2px solid ${this.getColor(theme, 'text')};
        }
        
        .cmp-modal__category {
          border: 2px solid ${this.getColor(theme, 'text')};
        }
        
        .cmp-modal__button {
          border: 2px solid currentColor;
        }
      }

      ${config.customCSS || ''}
    `;
  }

  private static getColor(theme: string, colorName: string): string {
    const themes = {
      light: {
        background: '#ffffff',
        cardBackground: '#f8fafc',
        text: '#1a1a1a',
        textSecondary: '#666666',
        primary: '#2563eb',
        primaryHover: '#1d4ed8',
        primaryLight: '#dbeafe',
        buttonSecondary: '#f8fafc',
        buttonSecondaryHover: '#f1f5f9',
        border: '#e2e8f0',
        toggleOff: '#cbd5e1',
        codeBackground: '#f1f5f9',
      },
      dark: {
        background: '#1f2937',
        cardBackground: '#374151',
        text: '#f9fafb',
        textSecondary: '#9ca3af',
        primary: '#3b82f6',
        primaryHover: '#2563eb',
        primaryLight: '#1e3a8a',
        buttonSecondary: '#374151',
        buttonSecondaryHover: '#4b5563',
        border: '#4b5563',
        toggleOff: '#6b7280',
        codeBackground: '#374151',
      },
      auto: {
        background: 'light-dark(#ffffff, #1f2937)',
        cardBackground: 'light-dark(#f8fafc, #374151)',
        text: 'light-dark(#1a1a1a, #f9fafb)',
        textSecondary: 'light-dark(#666666, #9ca3af)',
        primary: 'light-dark(#2563eb, #3b82f6)',
        primaryHover: 'light-dark(#1d4ed8, #2563eb)',
        primaryLight: 'light-dark(#dbeafe, #1e3a8a)',
        buttonSecondary: 'light-dark(#f8fafc, #374151)',
        buttonSecondaryHover: 'light-dark(#f1f5f9, #4b5563)',
        border: 'light-dark(#e2e8f0, #4b5563)',
        toggleOff: 'light-dark(#cbd5e1, #6b7280)',
        codeBackground: 'light-dark(#f1f5f9, #374151)',
      },
    };

    return (
      themes[theme as keyof typeof themes]?.[
        colorName as keyof typeof themes.light
      ] || themes.light[colorName as keyof typeof themes.light]
    );
  }
}
